import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'

import type { SearchModalItem } from '@common/components/SearchModal/search-modal.interface'

import { buildSearchEmptyState } from '../mappers/buildSearchEmptyState'
import { buildSearchModalProps } from '../mappers/buildSearchModalProps'
import { buildSearchModalSections } from '../mappers/buildSearchModalSections'
import { SEARCH_MODAL_FILTER_CHIP_IDS, searchModalFilterChips, searchModalTabs } from '../search-modal.const'
import type { AcademySearchController } from '../search-modal.interface'
import type { SearchModalFilterChipId, SearchModalTabId } from '../search-modal.type'
import { isEnabledFilterChipId, isSearchTabId } from '../search-modal.utils'

import { useDebouncedSearchQuery } from './useDebouncedSearchQuery'
import { useSearchPayload } from './useSearchPayload'

export function useAcademySearch(): AcademySearchController {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const { debouncedQuery, query, setDebouncedImmediately, setQuery } = useDebouncedSearchQuery()
  const { clearFetchError, fetchError, isLoading, payload } = useSearchPayload(open, debouncedQuery)
  const [activeTabId, setActiveTabId] = useState<SearchModalTabId>('academy')
  const [activeFilterChipId, setActiveFilterChipId] = useState<SearchModalFilterChipId>(
    SEARCH_MODAL_FILTER_CHIP_IDS.all
  )

  const openModal = useCallback(
    (next?: { query?: string }) => {
      if (next?.query !== undefined) {
        const q = next.query.trim()
        setQuery(q)
        setDebouncedImmediately(q)
      }

      clearFetchError()
      setActiveTabId('academy')
      setActiveFilterChipId(SEARCH_MODAL_FILTER_CHIP_IDS.all)
      setOpen(true)
    },
    [clearFetchError, setDebouncedImmediately, setQuery]
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      if (!next) {
        clearFetchError()
      }
    },
    [clearFetchError]
  )

  const sections = useMemo(
    () => buildSearchModalSections(activeFilterChipId, debouncedQuery, payload),
    [activeFilterChipId, debouncedQuery, payload]
  )

  const emptyStateText = useMemo(
    () =>
      buildSearchEmptyState({
        debouncedQueryTrimmed: debouncedQuery,
        fetchError,
        isLoading,
        sections,
      }),
    [debouncedQuery, fetchError, isLoading, sections]
  )

  const handleItemSelect = useCallback(
    (item: SearchModalItem) => {
      router.push(item.href)
      setOpen(false)
    },
    [router]
  )

  const handleTabChange = useCallback((id: string) => {
    if (isSearchTabId(id)) {
      setActiveTabId(id)
    }
  }, [])

  const handleFilterChipChange = useCallback((id: string) => {
    if (isEnabledFilterChipId(id)) {
      setActiveFilterChipId(id)
    }
  }, [])

  const searchModalProps = useMemo(
    () =>
      buildSearchModalProps({
        activeFilterChipId,
        activeTabId,
        emptyState: emptyStateText,
        filterChips: searchModalFilterChips,
        isLoading,
        onFilterChipChange: handleFilterChipChange,
        onItemSelect: handleItemSelect,
        onOpenChange: handleOpenChange,
        onQueryChange: setQuery,
        onTabChange: handleTabChange,
        open,
        query,
        sections,
        tabs: searchModalTabs,
      }),
    [
      activeTabId,
      activeFilterChipId,
      emptyStateText,
      handleFilterChipChange,
      handleItemSelect,
      handleOpenChange,
      handleTabChange,
      isLoading,
      open,
      query,
      sections,
      setQuery,
    ]
  )

  return {
    openModal,
    searchModalProps,
  }
}
